import React, { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX, Music } from "lucide-react";

// Free Lofi music stream URLs
const LOFI_TRACKS = [
  "https://streams.ilovemusic.de/iloveradio17.mp3", // Lofi Hip Hop stream
];

const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [volume, setVolume] = useState(0.3);
  const audioRef = useRef(null);

  useEffect(() => {
    // Check if user already accepted music
    const musicAccepted = localStorage.getItem("lofi_music_accepted");
    if (musicAccepted === "true") {
      setShowPrompt(false);
      // Don't auto-play, let user click to start
    }
  }, []);

  const startMusic = () => {
    localStorage.setItem("lofi_music_accepted", "true");
    setShowPrompt(false);
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.play().catch(err => {
        console.warn("Audio play failed:", err);
      });
      setIsPlaying(true);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => {
          console.warn("Audio play failed:", err);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  // Music prompt overlay
  if (showPrompt) {
    return (
      <div className="fixed bottom-20 left-4 z-50 animate-in slide-in-from-left-4 duration-500">
        <div className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-4 shadow-xl max-w-[280px]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Music size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm mb-1">Ambiance Lofi 🎵</p>
              <p className="text-white/80 text-xs mb-3">
                Activez la musique chill pour une meilleure expérience
              </p>
              <div className="flex gap-2">
                <button
                  onClick={startMusic}
                  className="px-3 py-1.5 bg-white text-violet-600 rounded-lg text-xs font-bold hover:bg-white/90 transition-all"
                >
                  Activer 🎧
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem("lofi_music_accepted", "false");
                    setShowPrompt(false);
                  }}
                  className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-xs font-medium hover:bg-white/30 transition-all"
                >
                  Non merci
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Audio element */}
        <audio
          ref={audioRef}
          src={LOFI_TRACKS[0]}
          loop
          preload="none"
        />
      </div>
    );
  }

  // Mini player (after prompt dismissed)
  const musicDeclined = localStorage.getItem("lofi_music_accepted") === "false";
  if (musicDeclined) return null;

  return (
    <div className="fixed bottom-20 left-4 z-50">
      <div 
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg transition-all duration-300 ${
          isPlaying 
            ? 'bg-gradient-to-r from-violet-500 to-purple-500' 
            : 'bg-white border border-slate-200'
        }`}
      >
        {/* Play/Pause button */}
        <button
          onClick={togglePlay}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isPlaying 
              ? 'bg-white/20 text-white hover:bg-white/30' 
              : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
          }`}
        >
          {isPlaying ? (
            <Volume2 size={16} className="animate-pulse" />
          ) : (
            <VolumeX size={16} />
          )}
        </button>

        {/* Volume slider (only when playing) */}
        {isPlaying && (
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-16 h-1 bg-white/30 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-3
              [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:bg-white
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:shadow-md"
          />
        )}

        {/* Label */}
        <span className={`text-xs font-medium ${isPlaying ? 'text-white' : 'text-slate-600'}`}>
          {isPlaying ? 'Lofi' : 'Musique'}
        </span>

        {/* Animated music bars when playing */}
        {isPlaying && (
          <div className="flex items-end gap-0.5 h-4 ml-1">
            <div className="w-1 bg-white/80 rounded-full animate-music-bar-1" style={{ animationDelay: '0ms' }} />
            <div className="w-1 bg-white/80 rounded-full animate-music-bar-2" style={{ animationDelay: '150ms' }} />
            <div className="w-1 bg-white/80 rounded-full animate-music-bar-3" style={{ animationDelay: '300ms' }} />
          </div>
        )}
      </div>

      {/* Audio element */}
      <audio
        ref={audioRef}
        src={LOFI_TRACKS[0]}
        loop
        preload="none"
      />

      {/* Custom CSS for music bars animation */}
      <style>{`
        @keyframes musicBar1 {
          0%, 100% { height: 4px; }
          50% { height: 16px; }
        }
        @keyframes musicBar2 {
          0%, 100% { height: 8px; }
          50% { height: 12px; }
        }
        @keyframes musicBar3 {
          0%, 100% { height: 6px; }
          50% { height: 14px; }
        }
        .animate-music-bar-1 {
          animation: musicBar1 0.8s ease-in-out infinite;
        }
        .animate-music-bar-2 {
          animation: musicBar2 0.6s ease-in-out infinite;
        }
        .animate-music-bar-3 {
          animation: musicBar3 0.7s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MusicPlayer;
